import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AssigneeModal({ open, onClose, onSuccess, sdpId, token, users, currentAssigneeType, currentAssigneeId }) {
  const [loading, setLoading] = useState(false);
  const [assigneeType, setAssigneeType] = useState(currentAssigneeType || "");
  const [assigneeId, setAssigneeId] = useState(currentAssigneeId || "");

  useEffect(() => {
    if (open) {
      setAssigneeType(currentAssigneeType || "");
      setAssigneeId(currentAssigneeId || "");
    }
  }, [open, currentAssigneeType, currentAssigneeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/sdp/${sdpId}`, {
        assignee_type: assigneeType || null,
        assignee_id: assigneeType === 'user' ? assigneeId : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Atribuicao alterada com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar atribuicao");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Alterar Atribuicao
          </DialogTitle>
          <DialogDescription>
            Selecione quem sera responsavel por esta SDP
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <RadioGroup 
            value={assigneeType} 
            onValueChange={(v) => {
              setAssigneeType(v);
              if (v !== 'user') {
                setAssigneeId('');
              }
            }}
            className="space-y-3"
          >
            <div className={`flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors ${assigneeType === 'admin' ? 'border-purple-500 bg-purple-50' : ''}`}>
              <RadioGroupItem value="admin" id="edit_assign_admin" />
              <Label htmlFor="edit_assign_admin" className="font-normal cursor-pointer flex-1">
                <span className="font-medium">Grupo Admin</span>
                <span className="text-slate-500 text-sm block">Atribuir a equipe de administracao</span>
              </Label>
            </div>
            <div className={`flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors ${assigneeType === 'engenharia' ? 'border-blue-500 bg-blue-50' : ''}`}>
              <RadioGroupItem value="engenharia" id="edit_assign_engenharia" />
              <Label htmlFor="edit_assign_engenharia" className="font-normal cursor-pointer flex-1">
                <span className="font-medium">Grupo Engenharia</span>
                <span className="text-slate-500 text-sm block">Atribuir a equipe de engenharia</span>
              </Label>
            </div>
            <div className={`flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors ${assigneeType === 'user' ? 'border-slate-500 bg-slate-50' : ''}`}>
              <RadioGroupItem value="user" id="edit_assign_user" />
              <Label htmlFor="edit_assign_user" className="font-normal cursor-pointer flex-1">
                <span className="font-medium">Usuario Especifico</span>
                <span className="text-slate-500 text-sm block">Atribuir a um usuario individual</span>
              </Label>
            </div>
          </RadioGroup>
          
          {assigneeType === 'user' && (
            <div className="pl-2">
              <Label>Selecione o usuario:</Label>
              <Select 
                value={assigneeId || "placeholder"} 
                onValueChange={(v) => setAssigneeId(v === "placeholder" ? "" : v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione um usuario..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Selecione um usuario...</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading || (assigneeType === 'user' && !assigneeId)}>
              {loading ? "Salvando..." : "Salvar Alteracao"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
